/**
 * Which wizard section an uploaded document belongs to (GDN-3386 follow-up).
 *
 * A clinic document (a W-9, a voided cheque) and a provider document (a state
 * licence, a CV) both land in the same inventory, so both used to appear on
 * both upload pages and to offer suggestions on every page. That reads as a
 * bug: the reader is asked to confirm a licence number on a clinic page.
 *
 * No backend change is needed to tell them apart. Each inventory document
 * carries the intake items it was linked to, and every intake item is either
 * organization-owned (`user_id == null`) or provider-owned. So a document's
 * section is the subject of the items it is linked to.
 */

export type IntakeDocumentSection = 'clinic_setup' | 'your_intake';

interface DashboardItemSubject {
  intake_item_id?: string | null;
  requirement_key?: string | null;
  user_id?: string | null;
}

interface DocumentItemLink {
  intake_item_id?: string | null;
  requirement_key?: string | null;
}

/**
 * The sections a document belongs to, in rail order.
 *
 * An empty result means "not known yet" — the document has no links because it
 * is still being read, or because nothing matched. Callers must treat that as
 * "show everywhere" rather than "show nowhere": hiding a document the reader
 * just uploaded is worse than showing it on both pages for a moment.
 */
export function documentSectionsFromLinks(
  links: readonly DocumentItemLink[] | undefined,
  items: readonly DashboardItemSubject[],
): IntakeDocumentSection[] {
  if (!links || links.length === 0) return [];
  const byItemId = new Map(
    items.flatMap(item => (item.intake_item_id ? [[item.intake_item_id, item] as const] : [])),
  );
  const byRequirementKey = new Map(
    items.flatMap(item => (item.requirement_key ? [[item.requirement_key, item] as const] : [])),
  );
  const sections = new Set<IntakeDocumentSection>();
  for (const link of links) {
    const item =
      (link.intake_item_id ? byItemId.get(link.intake_item_id) : undefined) ??
      (link.requirement_key ? byRequirementKey.get(link.requirement_key) : undefined);
    if (!item) continue;
    sections.add(item.user_id == null ? 'clinic_setup' : 'your_intake');
  }
  return (['clinic_setup', 'your_intake'] as const).filter(section => sections.has(section));
}

/** Whether a document with these sections belongs on `section`. Unknown belongs everywhere. */
export function documentBelongsToSection(
  documentSections: readonly IntakeDocumentSection[],
  section: IntakeDocumentSection,
): boolean {
  return documentSections.length === 0 || documentSections.includes(section);
}
